import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { SHIPPING_PRICES } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faPlus } from '@fortawesome/pro-regular-svg-icons';

/**
 * @author @muhammedburakak
 */

const AddPriceManuallyModal = NiceModal.create(({ recordType, shipmentNumber, invoiceStatus, currency }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const defaultColSpan = {
		md: 6
	};

	const [isLoading, setIsLoading] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					recordDescription: yup.string().nullable().required(),
					manualCharge: yup.number().nullable().positive().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			recordDescription: null,
			manualCharge: null
		}
	});

	const {
		setValue,
		handleSubmit,

		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const handleCloseModal = () => {
		modal.remove();
	};

	const onSubmit = (data) => {
		setIsLoading(true);
		axiosPrivate
			.post(SHIPPING_PRICES.ADD_PRICE_MANUALLY, {
				shipmentID: shipmentNumber,
				recordDescription: data.recordDescription,
				manualCharge: data.manualCharge
			})
			.then((response) => {
				if (response.status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.PRICE_NOT_FOUND_ORDERS));
					handleCloseModal();
				}
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	return (
		<Modal fullwidth maxWidth="medium" className="modal-addPriceManually">
			<ModalTitle>
				<Typography variant="p1">manualCharge</Typography>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods} borderless>
					<FormItem colSpan={defaultColSpan} label="recordType" isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							name="recordType"
							enumName="recordType"
							disabled={true}
							setValue={(value) => {
								setValue(`recordType`, value, { shouldValidate: true, shouldDirty: true });
							}}
							value={recordType}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="status">
						<Status value={invoiceStatus} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="manualCharge" isLoading={isLoading} required>
						<IntegerInput name="manualCharge" unit={currency} showArrows={false} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="shipmentNumber" isLoading={isLoading}>
						<StringInput disabled={true} value={shipmentNumber} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="recordDescription" isLoading={isLoading} required>
						<TextArea name="recordDescription" />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />

				<Button
					icon={faPlus}
					label="button.add"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					variant="blue"
					loading={isLoading}
				/>
			</ModalActions>
		</Modal>
	);
});

export default AddPriceManuallyModal;
